import {Action, action, Computed, computed, Thunk, thunk} from 'easy-peasy';
import axios from 'axios';
import slugify from 'slugify';
import {navigate} from '@/reach-router-compat';
import {Storyboard, StoryboardAccess} from '@/entities/storyboard';
import {Chart} from '@/entities/chart';
import {ROUTES} from 'routing/route';
import {StoreModel} from '@/store';
import {buildURL} from '@/utils/urlBuilder';
import {getStoryboardTabBasePathFromPath} from '@/routing/utils/getStoryboardTabBasePathFromPath';


export interface StoryboardsModel {
    items: Storyboard[],
    loading: boolean,
    getLoadingLock: Thunk<StoryboardsModel, boolean>,
    setLoading: Action<StoryboardsModel, boolean>,

    userIsStoryAdmin: Computed<StoryboardsModel, boolean, StoreModel>,
    userIsStoryOwner: Computed<StoryboardsModel, boolean, StoreModel>,
    storyTitle: Computed<StoryboardsModel, string, StoreModel>,
    storyIsPublic: Computed<StoryboardsModel, boolean, StoreModel>,

    setStoryboards: Thunk<StoryboardsModel, Storyboard[], null, StoreModel>,
    storeStoryboards: Action<StoryboardsModel, Storyboard[]>,
    setStoryboard: Action<StoryboardsModel, { storyboardId: number, storyboardData: Storyboard }>,

    addChart: Thunk<StoryboardsModel, [Chart, number | undefined], null, StoreModel>,
    saveNewChart: Action<StoryboardsModel, [chartData: Chart, toIndex?: number]>,

    editStoryboardAccess: Action<StoryboardsModel, [number, StoryboardAccess[]]>,
    deleteStoryboardAccess: Action<StoryboardsModel, [number, number]>,

    duplicateStory: Thunk<StoryboardsModel, number, null, StoreModel>,
    commitDummyStory: Thunk<StoryboardsModel, { publicCommit: boolean }, null, StoreModel>,

    reload: boolean,
    setReload: Action<StoryboardsModel, boolean>,
}


export default <StoryboardsModel>{
    items: [],
    loading: false,
    getLoadingLock: thunk((actions, loading, {getState}) => {
        if (getState().loading && loading) {
            return false;
        } else {
            actions.setLoading(loading);
        }
    }),
    setLoading: action((state, loading) => {
        state.loading = loading;
    }),

    userIsStoryAdmin: computed(
        [
            (state, storeState) => storeState.activeStoryboard.activeStoryboard,
        ],
        (activeStoryboard) => activeStoryboard?.userIsStoryAdmin ?? false,
    ),
    userIsStoryOwner: computed(
        [
            (state, storeState) => storeState.activeStoryboard.activeStoryboard,
        ],
        (activeStoryboard) => activeStoryboard?.userIsStoryOwner ?? false,
    ),
    storyTitle: computed(
        [
            (state, storeState) => storeState.activeStoryboard.activeStoryboard,
        ],
        (activeStoryboard) => activeStoryboard?.storyTitle ?? '',
    ),
    storyIsPublic: computed(
        [
            (state, storeState) => storeState.activeStoryboard.activeStoryboard,
        ],
        (activeStoryboard) => activeStoryboard?.storyIsPublic ?? false,
    ),
    setStoryboards: thunk((actions, storyboardList, {getStoreState, getStoreActions}) => {
        actions.storeStoryboards(storyboardList);

        const {activeStoryboard} = getStoreState().activeStoryboard;
        if (activeStoryboard?.globalFilters) {
            getStoreActions().search.setSearchQueryAllFields(activeStoryboard.globalFilters.all_fields);
            getStoreActions().search.setSearchQueryAdditionalFilters(activeStoryboard.globalFilters.additional_filters);
        }
    }),
    storeStoryboards: action((state, storyboardList) => {
        state.items = storyboardList;
    }),
    setStoryboard: action((state, {storyboardId, storyboardData}) => {
        const storyboardIndex = state.items.findIndex((el) => el.id === storyboardId);
        state.items[storyboardIndex] = storyboardData;
    }),
    addChart: thunk((actions, [chartData, toIndex], {getState, getStoreState, getStoreActions}) => {
        actions.saveNewChart([chartData, toIndex]);

        const fullStore = getStoreState();
        const storyboards = getState().items;

        if (chartData.storyboardId === fullStore.activeStoryboard.activeId) {
            getStoreActions().storyboardGridAnimation.setInGridAnimation();
        }

        const targetStoryboardIndex = storyboards.findIndex((sb) => sb.id === chartData.storyboardId);

        axios.post(window.URLS.chartReorder, {
            storyboardId: chartData.storyboardId,
            sortingOrder: storyboards[targetStoryboardIndex].charts.map((obj, idx) => ({
                id: obj.id,
                sortIndex: idx,
            })),
        });
    }),
    saveNewChart: action((state, [chartData, toIndex = 0]) => {
        let added = false;

        for (let i = 0; i < state.items.length; i++) {
            if (state.items[i].id === chartData.storyboardId) {
                state.items[i].charts.splice(toIndex, 0, chartData);

                added = true;
                break;
            }
        }

        if (!added) {
            throw new Error(`Chart was not added to state - storyboard ${chartData.storyboardId} not found`);
        }
    }),
    editStoryboardAccess: action((state, [storyboardId, storyboardAccesses]) => {
        const existingIndex = state.items.findIndex((sb) => sb.id === storyboardId);

        state.items[existingIndex].relatedUsers = storyboardAccesses;
    }),
    deleteStoryboardAccess: action((state, [storyboardId, userId]) => {
        const index = state.items.findIndex((sb) => sb.id === storyboardId);

        const existingIndex = state.items[index].relatedUsers.findIndex((s) => s.id === userId);

        state.items[index].relatedUsers.splice(existingIndex, 1);
    }),
    duplicateStory: thunk(async (actions, storyId, {getStoreState, getState}) => {
        const fullStore = getStoreState();
        const activeStoryboardIndex = fullStore.activeStoryboard.activeIndex;

        const storyCreateResponse = await axios.post(buildURL('cloneStory', {story_id: storyId}), {
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        const storyboardsResponse = await axios.get(window.URLS.storyboards, {
            params: {
                storyId: storyCreateResponse.data.id,
            },
        });

        actions.setStoryboards(storyboardsResponse.data);

        const updatedState = getState();
        const story = updatedState.items;
        const storyboard = story[activeStoryboardIndex];
        const {hashid} = storyboard;
        const slug = slugify(storyboard.title, {lower: true});
        const {activeDummyStorySlug} = getStoreState().activeDummyStory;
        const {activeStoryObject} = getStoreState().activeStoryObject;

        const currentPath = window.location.pathname;
        const basePath = getStoryboardTabBasePathFromPath(currentPath, activeDummyStorySlug);
        
        let toUrl;
        if (basePath) {
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.set('storyboard', hashid);
            searchParams.set('slug', slug);
            toUrl = `${basePath}?${searchParams.toString()}`;
        } else {
            toUrl = ROUTES.storyboardViewRoute
                .replace(':activeStoryboardHashid', hashid)
                .replace(':activeStoryboardUrl', slug);
            if (activeDummyStorySlug && activeStoryObject) {
                toUrl = `${toUrl}/${activeDummyStorySlug}/${activeStoryObject}`;
            }
        }

        await navigate(toUrl);
    }),
    commitDummyStory: thunk(async (actions, {publicCommit}, {
        getStoreState, getState, getStoreActions,
    }) => {
        const fullStore = getStoreState();

        if (getState().items[0].storyId < 0) {
            const activeStoryboardIndex = fullStore.activeStoryboard.activeIndex;

            const commitUrl = publicCommit ? 
                buildURL('publicCommitResource', { 
                    story_slug: fullStore.activeDummyStory.activeDummyStorySlug, 
                    storyboard_slug: fullStore.activeDummyStoryboard.activeDummyStoryboardSlug 
                }) :
                buildURL('privateCommitResource', { 
                    story_slug: fullStore.activeDummyStory.activeDummyStorySlug, 
                    storyboard_slug: fullStore.activeDummyStoryboard.activeDummyStoryboardSlug 
                });

            const response = await axios.post(commitUrl, {
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            });

            actions.setStoryboards(response.data);

            // Refresh public stories because an existing story was made public
            if (publicCommit) {
                const allActions = getStoreActions();
                const setPublicStoriesReload = allActions.publicStories.setReload;
                setPublicStoriesReload(true);
            }

            const updatedState = getState();
            const story = updatedState.items;
            const storyboard = story[activeStoryboardIndex];
            const {hashid} = storyboard;
            const slug = slugify(storyboard.title, {lower: true});

            const {activeDummyStorySlug} = getStoreState().activeDummyStory;
            const {activeStoryObject} = getStoreState().activeStoryObject;

            const currentPath = window.location.pathname;
            const basePath = getStoryboardTabBasePathFromPath(currentPath, activeDummyStorySlug);
            
            let toUrl;
            if (basePath) {
                const searchParams = new URLSearchParams(window.location.search);
                searchParams.set('storyboard', hashid);
                searchParams.set('slug', slug);
                toUrl = `${basePath}?${searchParams.toString()}`;
            } else {
                toUrl = ROUTES.storyboardViewRoute
                    .replace(':activeStoryboardHashid', hashid)
                    .replace(':activeStoryboardUrl', slug);
                if (activeDummyStorySlug && activeStoryObject) {
                    toUrl = `${toUrl}/${activeDummyStorySlug}/${activeStoryObject}`;
                }
            }

            await navigate(toUrl);
        }
    }),

    reload: false,
    setReload: action((state, value) => {
        state.reload = value;
    }),
};
